import Axios, { AxiosResponse } from "axios";
import { cachedGet, CachedGetOptions } from "@features/utils/axios";

import { TAccountData } from "@profile/pages/AccountPage/IAccountPage";
import IProfileData from "@profile/pages/AccountPage/IProfileData";

class ProfileServiceClass {
  public changePassword(accountData: TAccountData): Promise<AxiosResponse> {
    return Axios.post("/api/account/password_change", {
      _2fa_token: accountData.data.otp_code,
      new_password: accountData.data.new_password,
      password_confirmation: accountData.data.password_confirmation,
      old_password: accountData.data.old_password,
      log_out_of_other_devices:
        accountData.data.log_out_of_other_devices ?? false,
    });
  }

  public closeAccount(accountData: TAccountData): Promise<AxiosResponse> {
    const data = {
      _2fa_token: accountData.data.otp_code,
      password: accountData.data.old_password,
    };

    return Axios.patch("/api/account/close", data);
  }

  public markAsDormantClosing(
    accountData: TAccountData,
  ): Promise<AxiosResponse> {
    const data = {
      _2fa_token: accountData.data.otp_code,
      password: accountData.data.old_password,
    };

    return Axios.patch("/api/account/dormant_closing", data);
  }

  public changeEmail(accountData: TAccountData): Promise<AxiosResponse> {
    return Axios.patch("/api/account/emails", {
      email: accountData.data.email,
      _2fa_token: accountData.data.otp_code,
      current_password: accountData.data.old_password,
    });
  }

  public updateProfileData(profileData: IProfileData): Promise<AxiosResponse> {
    const { verification, profile, ...restData } = profileData;

    return Axios.patch("/api/account", {
      ...restData,
      account_data: profile,
    });
  }

  public resendConfirmationEmailChangeEmail(
    email: string,
  ): Promise<AxiosResponse> {
    // Devise endpoint, not /api: Rails CSRF protection applies, so send the token.
    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]',
    )?.content;

    return Axios.post(
      "/users/confirmation.json",
      { user: { email } },
      { headers: { "X-CSRF-Token": csrfToken } },
    );
  }

  public fetchProfileData(): Promise<AxiosResponse> {
    return Axios.get("/api/account");
  }

  public fetchUserData(options: CachedGetOptions = {}): Promise<AxiosResponse> {
    return cachedGet("/api/users/me", options);
  }

  public fetchSecuritySessions(): Promise<AxiosResponse> {
    return Axios.get("/api/security/sessions.json");
  }

  public fetchSecurityActivities(): Promise<AxiosResponse> {
    return Axios.get("/api/security/activities.json");
  }

  public fetchPasswordChangeStatus(): Promise<AxiosResponse> {
    return Axios.get("/api/account/password_change");
  }
}

const ProfileService = new ProfileServiceClass();

export default ProfileService;
